/**
 * Centralized SEO + social-share metadata for every route.
 *
 * Single source of truth used by:
 *  - <SEOHead /> at runtime (in-app navigation)
 *  - scripts/prerender-seo.mjs at build time (so crawlers see correct
 *    title/OG/Twitter/canonical tags in raw HTML for every route)
 *
 * Rules:
 *  - Titles <= 60 chars (truncates on Google + LinkedIn beyond that).
 *  - Descriptions <= 155 chars.
 *  - Lead with the dream destination, not pain.
 *  - Never put pricing or CPT codes in social previews.
 */

import { groupsFaqs } from "../data/faqs/groups";
import { CAREER_SPECS, type CareerSpec } from "../data/careers";
import { WHAT_IS_ENTRIES } from "../data/whatIsEntries";

export const SITE_ORIGIN = "https://solutions.beam.healthcare";
export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/og-image.png`;

export interface RouteSeoFaq {
  q: string;
  a: string;
}

export interface RouteSeoBreadcrumb {
  name: string;
  /** Path starting with "/" or a fully qualified URL. */
  url: string;
}

export interface RouteSeo {
  /** Route path. Use ":stateSlug" for the dynamic RHTP route. */
  path: string;
  title: string;
  description: string;
  /** Optional override; otherwise derived from SITE_ORIGIN + path. */
  canonical?: string;
  /** Optional per-route OG image. Falls back to DEFAULT_OG_IMAGE. */
  ogImage?: string;
  /** Optional JSON-LD payload (object or array of objects). */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /**
   * Optional FAQ entries. When present, the prerender plugin emits a
   * FAQPage JSON-LD block AND the runtime AudiencePage skips its
   * fallback FAQPage schema to avoid duplicates.
   */
  faqs?: RouteSeoFaq[];
  /**
   * Optional breadcrumb trail. When present, the prerender plugin emits
   * a BreadcrumbList JSON-LD block.
   */
  breadcrumbs?: RouteSeoBreadcrumb[];
  /**
   * Optional Service schema fields. When present, the prerender plugin
   * emits a Service JSON-LD block scoped to this route.
   */
  service?: { name: string; description: string };
  /**
   * Utility/transactional routes that should never be indexed or
   * submitted in the sitemap (thank-you pages, preference centers).
   */
  noindex?: boolean;
}


/**
 * Build a fully-qualified canonical URL for a given route path.
 */
export const canonicalFor = (path: string): string => {
  const clean = path.startsWith("/") ? path : `/${path}`;
  return `${SITE_ORIGIN}${clean === "/" ? "" : clean}`;
};

const orgJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Beam Healthcare",
  url: SITE_ORIGIN,
  logo: DEFAULT_OG_IMAGE,
  description:
    "Beam Healthcare provides same-day board-certified specialist access for primary care providers, clinics, and community hospitals.",
};

const websiteJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Beam Healthcare",
  url: SITE_ORIGIN,
};

export const ROUTE_SEO: Record<string, RouteSeo> = {
  "/beam-avel": { path: "/beam-avel", title: "Beam + Avel eCare | A Shared Vision for Local Care", description: "Explore a proposed Beam and Avel eCare care model for rural hospitals: emergency expertise, specialty access and support for local teams.", noindex: true },
  "/search": { path: "/search", title: "Search | Beam Healthcare", description: "Find services, tools, guides, and PACE programs on Beam Healthcare.", noindex: true },
  "/": {
    path: "/",
    title: "Beam Healthcare — Specialist Access, Patients Stay Close",
    description:
      "Solo providers, clinics, and community hospitals get the specialist input they need to treat more, refer less, and grow on their own terms.",
    jsonLd: [orgJsonLd, websiteJsonLd],
  },
  "/press": {
    path: "/press",
    title: "Press & Announcements | Beam Healthcare",
    description:
      "Official announcements, media coverage, and founder notes from Beam Healthcare — Joint Commission-accredited telehealth expanding specialist access nationwide.",
  },
  "/individual": {
    path: "/individual",
    title: "Curbside Consult with a Specialist, Same Day | Beam",
    description:
      "Get same-day input from a board-certified specialist on the case — so you can keep treating the patient instead of referring out.",
  },
  "/individual/nurse-practitioner": {
    path: "/individual/nurse-practitioner",
    title: "Nurse Practitioner Collaboration That Actually Helps | Beam",
    description:
      "Same-day specialist input for nurse practitioners on the cases that used to mean a referral — real collaboration, not a signature on a chart.",
  },
  "/individual/physician-associate": {
    path: "/individual/physician-associate",
    title: "Physician Associate Collaboration That Actually Helps | Beam",
    description:
      "Same-day specialist input for physician associates on the cases that used to mean a referral — real collaboration, not just a signature.",
  },
  "/individual/podiatrist": {
    path: "/individual/podiatrist",
    title: "Podiatrist Curbside Consults — ID, Endo & Wound Care | Beam",
    description:
      "Same-day board-certified ID, endo, and rheum curbsides for podiatrists — diabetic foot infections, non-healing wounds, and limb salvage.",
  },
  "/individual/direct-primary-care": {
    path: "/individual/direct-primary-care",
    title: "Direct Primary Care: Specialist Curbsides on Demand | Beam",
    description:
      "Built for DPC doctors who keep more in-house. Same-day board-certified specialist input — no referral roulette, no insurance friction. Pay-per-consult.",
  },
  "/groups": {
    path: "/groups",
    title: "Specialist eConsults for Healthcare Groups | Beam",
    description:
      "Give every provider on your team same-day specialist eConsults — keep patients in your ecosystem and capture the work you're already doing.",
    service: {
      name: "Beam Specialist eConsults for Healthcare Groups",
      description:
        "Structured specialist access for medical groups and clinics. Curbside consults, eConsults, and telespecialty solutions across your provider panel.",
    },
    faqs: groupsFaqs,
    breadcrumbs: [
      { name: "Home", url: "/" },
      { name: "For Healthcare Groups", url: "/groups" },
    ],
  },
  "/groups/pace": {
    path: "/groups/pace",
    title: "Specialist eConsults for PACE Organizations | Beam",
    description:
      "Same-day specialist input that helps your IDT keep frail elders in the community and lowers total cost of care under PACE capitation.",
  },
  "/inpatient-revenue-framework": {
    path: "/inpatient-revenue-framework",
    title: "Specialist Coverage for Rural & Community Hospitals | Beam",
    description:
      "Treat more patients in-house, get same-day specialist input, and capture every dollar of care your team is already delivering.",
  },
  "/inpatient-revenue-framework/specialty-consult": {
    path: "/inpatient-revenue-framework/specialty-consult",
    title: "Inpatient Specialty Consult Coverage, Any Acuity | Beam",
    description:
      "From a quick curbside to a full bedside consult — the right specialist input for every inpatient case, without the transfer.",
  },
  "/inpatient-revenue-framework/tele-case-manager": {
    path: "/inpatient-revenue-framework/tele-case-manager",
    title: "Inpatient Tele-Case Manager Coverage | Beam Healthcare",
    description:
      "Part of Beam's inpatient revenue framework: on-demand RN case managers help your team get observation vs. inpatient status right, in real time.",
  },
  "/tele-case-management": {
    path: "/tele-case-management",
    title: "Tele-Case Management Service, 7 Days a Week | Beam",
    description:
      "Standalone tele-case management from US-based RNs with 3+ years experience. Same-app status support and guideline-based documentation, 7am–11pm CST.",
  },
  "/tele-respiratory-therapy": {
    path: "/tele-respiratory-therapy",
    title: "Tele-Respiratory Therapy (Tele-RT) | Beam Healthcare",
    description:
      "Premium tele-respiratory therapy from credentialed RRTs with 3+ years experience. 24/7 vent management, weaning, and coverage for community and critical-access hospitals.",
  },
  "/tele-respiratory-therapy-partners": {
    path: "/tele-respiratory-therapy-partners",
    title: "Virtual Respiratory Care Partnerships | Beam Healthcare",
    description:
      "Beam runs the respiratory clinical operations behind digital health platforms, connected devices, employer health programs, and home-based care. Device-agnostic.",
  },
  "/tele-hospitalist": {
    path: "/tele-hospitalist",
    title: "Telemedicine Hospitalist Coverage for Hospitals | Beam",
    description:
      "Telemedicine hospitalist coverage from NPI-verified, board-certified internists with 3+ years post-residency — rounding, nocturnist, and cross-coverage.",
  },
  "/tele-stroke": {
    path: "/tele-stroke",
    title: "Tele-Stroke Services for Rural & Community EDs | Beam Healthcare",
    description:
      "Premium tele-stroke: board-certified vascular neurologists on video in minutes. NPI-verified, 3+ years post-residency. tPA and thrombectomy decision support 24/7.",
  },
  "/careers": {
    path: "/careers",
    title: "Telemedicine Careers — Join a Premium Specialist Panel | Beam",
    description:
      "Apply to Beam's premium telemedicine panel. NPI-verified, minimum 3 years post-residency. Physician and allied openings across specialties — work from anywhere in the US.",
  },
  "/what-is": {
    path: "/what-is",
    title: "Telemedicine Glossary — Plain-English Terms | Beam",
    description:
      "Plain-English definitions of telemedicine, tele-cardiology, tele-hospitalist, tele-stroke, tele-case management, and more from Beam Healthcare.",
  },
  "/inpatient-revenue-framework/tele-utilization-management": {
    path: "/inpatient-revenue-framework/tele-utilization-management",
    title: "Inpatient UM Coverage in the Revenue Framework | Beam",
    description:
      "Where utilization management fits in Beam's inpatient revenue framework — concurrent review that protects status determinations already made.",
  },
  // Legacy aliases resolve to the revenue framework on the primary domain.
  "/tele-case-manager": {
    path: "/tele-case-manager",
    canonical: `${SITE_ORIGIN}/inpatient-revenue-framework/tele-case-manager`,
    title: "Tele Case Manager for ED & Inpatient Status | Beam",
    description:
      "Live case manager support for observation vs. inpatient status determination — guideline-based documentation that holds up on review.",
  },
  "/tele-utilization-management": {
    path: "/tele-utilization-management",
    canonical: `${SITE_ORIGIN}/inpatient-revenue-framework/tele-utilization-management`,
    title: "Utilization Review Services for Hospitals | Beam",
    description:
      "Concurrent utilization review that captures the full clinical picture — so observation vs. inpatient status reflects the care you actually delivered.",
  },
  "/specialty-consult": {
    path: "/specialty-consult",
    canonical: `${SITE_ORIGIN}/inpatient-revenue-framework/specialty-consult`,
    title: "Inpatient Specialty Consults | Beam Healthcare",
    description:
      "Specialist support for hospital teams: curbside answers, written eConsults, and tele-clinics. Keep more care close to home.",
  },
  "/hospital-curbside-econsult": {
    path: "/hospital-curbside-econsult",
    title: "Hospital Curbside & eConsult for ED Teams | Beam",
    description:
      "Same-day board-certified curbside and eConsult for ED and inpatient teams. Keep the patient, protect the revenue, document the decision.",
  },
  "/inpatient-clinical-services": {
    path: "/inpatient-clinical-services",
    title: "Telehospitalist & Inpatient Clinical Services | Beam",
    description:
      "Telehospitalists, specialists, respiratory, and case management — modular clinical coverage that scales with your community hospital.",
  },
  "/rural-health-transformation-program-rhtp": {
    path: "/rural-health-transformation-program-rhtp",
    title: "RHTP Funding: Build a Lasting Specialist Network | Beam",
    description:
      "Turn Rural Health Transformation Program funding into shared specialist infrastructure across your state — and keep care close to home.",
  },
  "/resources": {
    path: "/resources",
    title: "Resources — Beam Healthcare",
    description:
      "Plain-English guides on eConsult billing, curbside care, and how rural providers keep patients close to home.",
    breadcrumbs: [
      { name: "Home", url: "/" },
      { name: "Resources", url: "/resources" },
    ],
  },
  "/resources/cpt-99451-99452-explained": {
    path: "/resources/cpt-99451-99452-explained",
    title: "CPT 99451 & 99452 Explained: eConsult Billing | Beam",
    description:
      "What CPT 99451 and 99452 cover, who can bill them, time thresholds, and how interprofessional eConsults actually get reimbursed.",
  },
  "/resources/curbside-consult-vs-econsult": {
    path: "/resources/curbside-consult-vs-econsult",
    title: "Curbside Consult vs eConsult: What's the Difference? | Beam",
    description:
      "Curbside consult vs eConsult — what each one means, when each is billable, and which one keeps your patient with you instead of going to a referral.",
  },
  "/resources/three-types-of-specialist-consults": {
    path: "/resources/three-types-of-specialist-consults",
    title: "Curbside vs eConsult vs Tele-consult: The 3 Types | Beam",
    description:
      "The three ways to get specialist input — curbside, eConsult, and tele-consult — compared side by side: format, documentation, and billing.",
  },
  "/resources/outpatient-tele-specialty-clinic": {
    path: "/resources/outpatient-tele-specialty-clinic",
    title: "What Is an Outpatient Tele-Specialty Clinic? | Beam",
    description:
      "How a tele-specialty clinic works: a board-certified specialist sees the patient by video inside your clinic, billed under standard outpatient E/M codes.",
  },
  "/resources/how-econsults-get-billed": {
    path: "/resources/how-econsults-get-billed",
    title: "How an eConsult Actually Gets Billed (Step by Step) | Beam",
    description:
      "The full eConsult billing workflow — consent, case prep, specialist report, and the two CPT codes (99451 and 99452) that close the loop.",
  },
  "/resources/curbside-impact-on-mdm-and-billing": {
    path: "/resources/curbside-impact-on-mdm-and-billing",
    title: "Can a Curbside Consult Raise Your Billing Level? | Beam",
    description:
      "A curbside isn't billable on its own — but the reasoning it informs is part of your MDM and can (not will) support a higher E/M level under 2021 rules.",
  },
  "/resources/medical-decision-making-curbside": {
    path: "/resources/medical-decision-making-curbside",
    title: "Medical Decision-Making in a Curbside Consult | Beam",
    description:
      "How to think about MDM, documentation, and liability when a specialist's curbside input shapes your plan — and when the question becomes an eConsult.",
  },
  "/resources/build-a-specialist-consult-program": {
    path: "/resources/build-a-specialist-consult-program",
    title: "How to Build a Specialist Consult Program | Beam",
    description:
      "What to staff, document, and measure to layer curbside, eConsult, and tele-specialty access into a working specialist program in your clinic.",
  },
  "/resources/ship-grants-for-rural-health-clinics": {
    path: "/resources/ship-grants-for-rural-health-clinics",
    title: "SHIP Grants for Rural Health Clinics: How to Use Them | Beam",
    description:
      "How hospital-affiliated Rural Health Clinics can use SHIP grant funds to stand up a specialist curbside and eConsult program under value-based care.",
  },
  "/resources/what-is-an-econsult": {
    path: "/resources/what-is-an-econsult",
    title: "What Is an eConsult? Plain-English Guide for Providers | Beam",
    description:
      "An eConsult is a written specialist opinion delivered through the chart. Here's how it works, what it bills under, and when to use one.",
  },
  "/resources/how-to-document-a-curbside-consult": {
    path: "/resources/how-to-document-a-curbside-consult",
    title: "How to Document a Curbside Consult (Without Risk) | Beam",
    description:
      "What to include, what to leave out, and when an informal curbside consult should become a documented, billable eConsult.",
  },
  "/resources/nurse-practitioner-collaborating-physician-guide": {
    path: "/resources/nurse-practitioner-collaborating-physician-guide",
    title: "Nurse Practitioner Collaborating Physician: 2026 Guide | Beam",
    description:
      "How NPs find a collaborating physician, what to put in the agreement, full vs reduced vs restricted practice, and where specialist eConsults fit.",
  },
  "/resources/cpt-99451-deep-dive": {
    path: "/resources/cpt-99451-deep-dive",
    title: "CPT 99451: The Specialist's eConsult Code, in Depth | Beam",
    description:
      "CPT 99451 covers a specialist's written interprofessional consult of 5+ minutes. Time, payment, documentation, and frequency rules explained.",
  },
  "/resources/cpt-99452-deep-dive": {
    path: "/resources/cpt-99452-deep-dive",
    title: "CPT 99452: The Treating Provider's eConsult Code | Beam",
    description:
      "CPT 99452 covers 16+ minutes of preparing and referring an interprofessional consult. Payer coverage, documentation rules, and the economics.",
  },
  "/resources/rural-health-transformation-program-explained": {
    path: "/resources/rural-health-transformation-program-explained",
    title: "What Is the Rural Health Transformation Program? | Beam",
    description:
      "A plain-English overview of RHTP — what it funds, how states use it, and where specialist access fits in a multi-year transformation plan.",
  },
  "/resources/econsult-roi-for-clinics": {
    path: "/resources/econsult-roi-for-clinics",
    title: "eConsult ROI for Clinics: the Math That Matters | Beam",
    description:
      "Per-consult code revenue, platform fees, referral avoidance, and the three KPIs to track for a real eConsult program in your clinic.",
  },
  "/resources/specialist-coverage-for-community-hospitals": {
    path: "/resources/specialist-coverage-for-community-hospitals",
    title: "Specialist Coverage for Community Hospitals | Beam",
    description:
      "Why community hospitals can't recruit a full bench — and how a 1–4 escalation model keeps the middle-band cases in-house and out of unnecessary transfers.",
  },
  "/resources/hospital-telehealth-program-guide": {
    path: "/resources/hospital-telehealth-program-guide",
    title: "Hospital Telehealth Program Guide: Design, Cost, Partner | Beam",
    description:
      "A plain-English guide for hospital leaders: design your telehealth program layer by layer, build the business case, and ask partners the right questions.",
  },
  "/resources/tele-hospitalist-vs-locum-tenens": {
    path: "/resources/tele-hospitalist-vs-locum-tenens",
    title: "Tele-Hospitalist vs Locum Tenens: Cost & Continuity | Beam",
    description:
      "Compare tele-hospitalist coverage to locum tenens on cost, continuity, EHR integration, and fit — a plain-language guide for hospital administrators.",
  },
  "/resources/what-is-direct-primary-care": {
    path: "/resources/what-is-direct-primary-care",
    title: "What Is Direct Primary Care? A Plain-English Guide | Beam",
    description:
      "Direct Primary Care (DPC) is a flat monthly fee for primary care with no insurance in the middle. How it works, what it covers, and where specialists fit.",
  },
  "/resources/specialist-referrals-in-direct-primary-care": {
    path: "/resources/specialist-referrals-in-direct-primary-care",
    title: "Specialist Referrals in Direct Primary Care | Beam",
    description:
      "How DPC doctors handle specialist input without pushing patients back to fee-for-service — curbside, eConsult, and full referral, side by side.",
  },
  "/resources/specialist-access-for-rural-health-clinics": {
    path: "/resources/specialist-access-for-rural-health-clinics",
    title: "Specialist Access for Rural Health Clinics (RHCs) | Beam",
    description:
      "Rural Health Clinics were built for primary care access. Same-day curbside and eConsult closes the specialty gap without adding staff.",
  },
  "/critical-access-hospitals": {
    path: "/critical-access-hospitals",
    title: "Critical Access Hospitals by State: CMS List | Beam",
    description:
      "Find critical access hospitals by state, city and county. Browse CMS hospital lists, ownership and ER services. Data retrieved August 18, 2026.",
    breadcrumbs: [
      { name: "Home", url: "/" },
      { name: "Critical Access Hospitals", url: "/critical-access-hospitals" },
    ],
  },
  "/pace-organizations": {
    path: "/pace-organizations",
    title: "PACE Programs by State: Map & Directory | Beam",
    description:
      "Browse PACE programs by state, name and city using September 2026 CMS data. View program profiles and official sites where verified. Check ZIP eligibility.",
    breadcrumbs: [
      { name: "Home", url: "/" },
      { name: "PACE Organizations", url: "/pace-organizations" },
    ],
  },
  "/resources/what-is-a-critical-access-hospital": {
    path: "/resources/what-is-a-critical-access-hospital",
    title: "What Is a Critical Access Hospital? Plain-English Guide",
    description:
      "CAH status explained: the 25-bed and 96-hour rules, distance tests, cost-based Medicare payment, and why some CAHs sit in metropolitan counties.",
  },
  "/resources/how-critical-access-hospitals-get-paid": {
    path: "/resources/how-critical-access-hospitals-get-paid",
    title: "How Critical Access Hospitals Get Paid by Medicare",
    description:
      "Cost-based reimbursement explained: the 101% rule, the Medicare cost report, sequestration, why CAH margins stay thin, and what actually moves them.",
  },
  "/resources/what-is-utilization-management": {
    path: "/resources/what-is-utilization-management",
    title: "What Is Utilization Management? A Hospital Guide",
    description:
      "Prior auth, admission status, InterQual and MCG, the two-midnight rule, and the denial and appeal workflow \u2014 plus who staffs utilization review.",
  },
  "/resources/what-does-a-hospital-case-manager-do": {
    path: "/resources/what-does-a-hospital-case-manager-do",
    title: "What Does a Hospital Case Manager Do? Duties, Day & Skills",
    description:
      "A hospital case manager plans safe discharges, reviews admissions, and lines up rehab or home care. See their daily duties, skills, and how they cut extra hospital days.",
  },
  "/resources/econsults-for-rural-health-clinics": {

    path: "/resources/econsults-for-rural-health-clinics",
    title: "eConsults for Rural Health Clinics: 99451/99452 & the AIR | Beam",
    description:
      "How CPT 99451/99452 flow through the RHC all-inclusive rate, where value shows up, and how RHCs fund a specialist eConsult program.",
  },
  "/resources/pace": {
    path: "/resources/pace",
    title: "PACE Resources: Capitation, IDT & Specialist Access | Beam",
    description:
      "A plain-English resource hub for PACE organizations — how the program works, how the interdisciplinary team runs, and how to close the specialty-access gap.",
  },
  "/resources/pace/explained": {
    path: "/resources/pace/explained",
    title: "What Is PACE? Eligibility, Costs & Services Explained (2026)",
    description:
      "PACE is a Medicare and Medicaid program that helps adults 55+ stay at home instead of a nursing home. See who qualifies, what it covers, and how it is paid.",
  },
  "/resources/pace/specialist-access": {
    path: "/resources/pace/specialist-access",
    title: "Specialist Access for PACE Organizations | Beam",
    description:
      "How PACE programs get same-day specialist input without shipping frail participants out of the day center — curbside, eConsult, and IDT workflow.",
  },
  "/resources/pace/interdisciplinary-team-workflows": {
    path: "/resources/pace/interdisciplinary-team-workflows",
    title: "PACE IDT Workflows: Daily Huddle, Weekly Care Plan | Beam",
    description:
      "How a PACE interdisciplinary team actually runs — daily huddle, weekly care-plan meeting, semi-annual reassessment, and where specialist input enters the IDT.",
  },
  "/resources/pace/rural-pace": {
    path: "/resources/pace/rural-pace",
    title: "Rural PACE: Operating the Model Outside Metro Areas | Beam",
    description:
      "How rural PACE organizations manage longer transport legs, thin IDT staffing, and specialty access 60–120 miles away — with practical operational patterns.",
  },
  "/resources/how-to-start-a-direct-primary-care-practice": {
    path: "/resources/how-to-start-a-direct-primary-care-practice",
    title: "How to Start a Direct Primary Care Practice: 90-Day Guide | Beam",
    description:
      "Legal, operational, and clinical decisions to make before your first DPC member — with break-even math and the specialist pathway most launches skip.",
  },
  "/resources/dpc-vs-concierge-medicine": {
    path: "/resources/dpc-vs-concierge-medicine",
    title: "DPC vs Concierge Medicine: What's the Difference? | Beam",
    description:
      "Direct Primary Care and concierge medicine both charge a monthly fee. Only one gets you out of insurance billing. Side-by-side breakdown.",
  },
  "/resources/specialist-support-for-rural-nurse-practitioners": {
    path: "/resources/specialist-support-for-rural-nurse-practitioners",
    title: "Specialist Support for Rural Nurse Practitioners | Beam",
    description:
      "Same-day curbside and eConsult access for rural NPs — sits on top of the collaborating physician relationship, not in place of it.",
  },
  "/resources/specialist-support-for-rural-physician-assistants": {
    path: "/resources/specialist-support-for-rural-physician-assistants",
    title: "Specialist Support for Rural Physician Associates | Beam",
    description:
      "How rural PAs add specialty-specific reasoning on top of the SP relationship with same-day curbside and documented eConsults.",
  },
  "/resources/rural-primary-care-specialist-access": {
    path: "/resources/rural-primary-care-specialist-access",
    title: "Rural Primary Care & Specialist Access | Beam",
    description:
      "Rural patients see specialists half as often as urban patients. Here's how same-day specialty access keeps care local without losing the patient.",
  },
  "/resources/curbside-consults-for-nurse-practitioners": {
    path: "/resources/curbside-consults-for-nurse-practitioners",
    title: "Curbside Consults for Nurse Practitioners | Beam",
    description:
      "When a curbside consult is right for an NP, when to switch to a documented eConsult, and how it layers on top of your collaborating physician.",
  },
  "/resources/curbside-consults-for-physician-assistants": {
    path: "/resources/curbside-consults-for-physician-assistants",
    title: "Curbside Consults for Physician Associates | Beam",
    description:
      "How PAs use a specialist curbside appropriately, when to convert to an eConsult (CPT 99451/99452), and how it sits alongside the SP relationship.",
  },
  "/resources/collaborating-physician-vs-specialist-consult": {
    path: "/resources/collaborating-physician-vs-specialist-consult",
    title: "Collaborating Physician vs Specialist Consult | Beam",
    description:
      "One is a state-required legal relationship. The other is a clinical pathway to specialty reasoning. Here's how they layer for NPs and PAs.",
  },


  "/cardiology-reads": {
    path: "/cardiology-reads",
    title: "Cardiology Reads & Clinical Support | Beam Healthcare",
    description:
      "Beam provides cardiology interpretations with clinical support, abnormal-result guidance, and program-level partnership for hospitals that need more than read-only reports.",
  },
  "/pulmonary-function-test-reads": {
    path: "/pulmonary-function-test-reads",
    title: "Pulmonary Function Test Reads | Beam Healthcare",
    description:
      "PFT interpretations from board-certified pulmonologists: one business day turnaround, spirometry, DLCO and lung volumes read to standard, plus a pulmonologist available for questions.",
  },
  "/tele-endocrinology": {
    path: "/tele-endocrinology",
    title: "Tele-Endocrinology for Rural Hospitals & Clinics | Beam Healthcare",
    description:
      "Premium tele-endocrinology from NPI-verified, board-certified endocrinologists with 3+ years post-residency. Curbside, eConsult, or tele-visit — diabetes, thyroid, adrenal.",
  },
  "/tele-rheumatology": {
    path: "/tele-rheumatology",
    title: "Tele-Rheumatology for Rural Hospitals & Clinics | Beam Healthcare",
    description:
      "Premium tele-rheumatology from NPI-verified, board-certified rheumatologists with 3+ years post-residency. Curbside, eConsult, or tele-visit — skip the six-month wait.",
  },
  "/tele-cardiology": {
    path: "/tele-cardiology",
    title: "Tele-Cardiology for Rural Hospitals & Clinics | Beam Healthcare",
    description:
      "Premium tele-cardiology from NPI-verified, board-certified cardiologists with 3+ years post-residency. Curbside, eConsult, or tele-visit — keep cardiac care local.",
  },
  "/tele-pulmonology": {
    path: "/tele-pulmonology",
    title: "Tele-Pulmonology for Rural Hospitals & Clinics | Beam Healthcare",
    description:
      "Premium tele-pulmonology from NPI-verified, board-certified pulmonologists with 3+ years post-residency. Curbside, eConsult, or tele-visit — COPD to nodules.",
  },
  "/tele-nephrology": {
    path: "/tele-nephrology",
    title: "Tele-Nephrology for Rural Hospitals & Clinics | Beam Healthcare",
    description:
      "Premium tele-nephrology from NPI-verified, board-certified nephrologists with 3+ years post-residency. CKD, AKI, and electrolytes without losing patients to referral.",
  },
  "/tele-hematology": {
    path: "/tele-hematology",
    title: "Tele-Hematology for Rural Hospitals | Beam Healthcare",
    description:
      "Premium tele-hematology from NPI-verified, board-certified hematologists. Anemia, low platelets, clotting, and anticoagulation support — stabilize locally, avoid transfers.",
  },
  "/tele-palliative-care": {
    path: "/tele-palliative-care",
    title: "Tele-Palliative Care for Rural Hospitals & Clinics | Beam Healthcare",
    description:
      "Curbside, eConsult, or tele-visit — Beam's tiered tele-palliative care supports symptom management, prognosis, hospice eligibility, and goals-of-care conversations for hospitals, EDs, and clinics.",
  },

  "/tele-neurology": {
    path: "/tele-neurology",
    title: "Tele-Neurology for Rural Hospitals & Clinics | Beam Healthcare",
    description:
      "Premium tele-neurology from NPI-verified, board-certified neurologists with 3+ years post-residency. Headache, seizure, TIA, and neuropathy — no nine-month wait.",
  },
  "/dermatology": {
    path: "/dermatology",
    title: "Dermatology Curbsides for Rural Hospitals & Clinics | Beam Healthcare",
    description:
      "Premium dermatology curbsides from NPI-verified, board-certified dermatologists with 3+ years post-residency. Send the photo, get the answer — no tele-visit needed.",
  },
  "/reh-vs-cah": {
    path: "/reh-vs-cah",
    title: "Beam Healthcare — REH vs CAH Assessment Tool",
    description:
      "Free self-assessment and side-by-side financial calculator for rural hospital leaders weighing CAH to REH conversion.",
  },
  "/join-the-beam-team/specialists": {
    path: "/join-the-beam-team/specialists",
    title: "Join the Beam Team — Specialists | Beam Healthcare",
    description:
      "Practice from anywhere. Help rural patients get the specialist input they need, on a flexible schedule that fits your life.",
  },
  "/client/curbside-vs-econsult": {
    path: "/client/curbside-vs-econsult",
    title: "Curbside vs eConsult — Client Guide | Beam Healthcare",
    description:
      "A private orientation for Beam clients: when to use a curbside, when to use an eConsult, sample specialist notes, and every supporting resource in one place.",
    noindex: true,
  },
  "/add-payment-method": {
    path: "/add-payment-method",
    title: "Add a Payment Method | Beam Healthcare",
    description:
      "Save a payment method so you can keep using Beam past your 2 free curbsides each month.",
    noindex: true,
  },
  "/success": {
    path: "/success",
    title: "Payment Method Saved | Beam Healthcare",
    description:
      "Your payment method is saved with Beam Healthcare. Head back to the app and keep sending consults.",
    noindex: true,
  },

  "/terms-of-service": {
    path: "/terms-of-service",
    title: "Terms of Service | Beam Healthcare",
    description:
      "Read the Beam Healthcare Terms of Service covering acceptable use, billing, specialist consult scope, and provider responsibilities.",
  },
  "/privacy-policy": {
    path: "/privacy-policy",
    title: "Privacy Policy | Beam Healthcare",
    description:
      "Learn how Beam Healthcare collects, uses, and protects your data, what we never store, and how consult information is handled.",
  },
  "/unsubscribe": {
    path: "/unsubscribe",
    title: "Manage Email Preferences | Beam Healthcare",
    description: "Update or unsubscribe from Beam Healthcare emails.",
    noindex: true,
  },

  "/tools": {
    path: "/tools",
    title: "Free Hospital & DPC Calculators | Beam Healthcare",
    description:
      "Free vetted calculators for hospital operations, hospital finance, and Direct Primary Care startups — every formula sourced to CMS, HFMA, ACEP, or the DPC Alliance.",
    breadcrumbs: [
      { name: "Home", url: "/" },
      { name: "Tools", url: "/tools" },
    ],
  },
  "/tools/average-length-of-stay": {
    path: "/tools/average-length-of-stay",
    title: "Average Length of Stay Calculator: ALOS Formula + Benchmarks",
    description:
      "Calculate hospital average length of stay in seconds: patient days ÷ discharges. Compare to GMLOS and rural, community, and academic benchmarks. Free.",
  },
  "/tools/bed-occupancy": {
    path: "/tools/bed-occupancy",
    title: "Bed Occupancy Rate & ADC Calculator | Beam Healthcare",
    description:
      "Free calculator for hospital bed occupancy rate and average daily census (ADC) using the AHA formula. Includes rural and community hospital benchmarks.",
  },
  "/tools/readmission-rate": {
    path: "/tools/readmission-rate",
    title: "Readmission Rate Calculator: 30-Day Formula & HRRP Penalty",
    description:
      "How to calculate hospital readmission rate: readmissions ÷ discharges × 100. Free calculator shows your 30-day rate, national average, and CMS penalty risk.",
  },
  "/tools/ed-boarding-cost": {
    path: "/tools/ed-boarding-cost",
    title: "ED Boarding Cost Calculator | Beam Healthcare",
    description:
      "Estimate the monthly and annual revenue lost to ED boarding using the Falvo/ACEP opportunity-cost model plus LWBS margin loss.",
  },
  "/tools/avoidable-transfer-cost": {
    path: "/tools/avoidable-transfer-cost",
    title: "Avoidable Patient Transfer Cost Calculator | Beam",
    description:
      "Free calculator for rural and community hospitals: estimate the revenue and cost lost each year to patient transfers that could have stayed.",
  },
  "/tools/pace-outside-specialist-cost": {
    path: "/tools/pace-outside-specialist-cost",
    title: "PACE Outside Specialist Referral Cost Calculator | Beam",
    description:
      "Free calculator for PACE organizations: estimate yearly outside specialist claims and ride costs, and how much stays in-center.",
  },
  "/resources/how-pace-organizations-manage-specialty-care": {
    path: "/resources/how-pace-organizations-manage-specialty-care",
    title: "How PACE Organizations Manage Specialty Care | Beam",
    description:
      "Plain-English guide to PACE specialist referrals: what outside visits cost and which ones can be handled at the center.",
  },
  "/tools/hospitalist-roi": {
    path: "/tools/hospitalist-roi",
    title: "Hospitalist ROI Calculator: Locum vs Tele-Hospitalist | Beam",
    description:
      "Free calculator: compare the yearly cost of locum tenens hospitalist shifts with tele-hospitalist coverage and see your savings.",
  },
  "/resources/two-midnight-rule-and-observation-status": {
    path: "/resources/two-midnight-rule-and-observation-status",
    title: "Two-Midnight Rule & Observation Status, Explained | Beam",
    description:
      "Plain-English guide to the Medicare two-midnight rule, observation vs inpatient status, the MOON notice, and how case management protects hospital revenue.",
  },
  "/tools/days-cash-on-hand": {
    path: "/tools/days-cash-on-hand",
    title: "Days Cash on Hand Calculator: Formula & Hospital Benchmarks",
    description:
      "Days cash on hand = cash ÷ (operating expenses − depreciation) ÷ 365. Free calculator with Moody's and rural hospital benchmarks to see where you stand.",
  },
  "/tools/case-mix-index": {
    path: "/tools/case-mix-index",
    title: "Case Mix Index Calculator: How to Calculate CMI (Free)",
    description:
      "Case mix index = total DRG weights ÷ discharges. Free CMI calculator with the national Medicare average and what a high or low CMI means for revenue.",
  },
  "/tools/340b-savings-estimator": {
    path: "/tools/340b-savings-estimator",
    title: "340B Drug Program Savings Estimator | Beam Healthcare",
    description:
      "Free planning estimator for 340B Drug Pricing Program savings using HRSA ceiling-price methodology (AMP − URA). Model different discount scenarios.",
  },
  "/tools/dpc-panel-revenue": {
    path: "/tools/dpc-panel-revenue",
    title: "DPC Panel & Revenue Calculator (Break-Even) | Beam",
    description:
      "Direct Primary Care revenue, net income, and break-even panel-size calculator. Uses the DPC Alliance / AAFP DPC financial model.",
  },
  "/tools/dpc-startup-cost": {
    path: "/tools/dpc-startup-cost",
    title: "DPC Startup Cost Calculator (7 Buckets) | Beam Healthcare",
    description:
      "Line-item Direct Primary Care startup cost estimator with the seven categories the AAFP DPC toolkit and DPC Frontier consistently identify.",
  },
  "/tools/nurse-staffing-ratio": {
    path: "/tools/nurse-staffing-ratio",
    title: "Nurse Staffing Calculator: Nurse-to-Patient Ratio & FTEs",
    description:
      "Free nurse staffing calculator: estimate nurses per shift, daily nurse-shifts, and annual FTEs using patient count, target ratio, HPPD, and shift length.",
  },

  "/tools/wrvu-compensation": {
    path: "/tools/wrvu-compensation",
    title: "wRVU Calculator: Physician Compensation per RVU (2026)",
    description:
      "Turn wRVUs into pay. Free physician compensation calculator with base salary, dollars per wRVU, production bonus, and quality bonus. See your total in seconds.",
  },
  "/tools/ed-lwbs-revenue-loss": {
    path: "/tools/ed-lwbs-revenue-loss",
    title: "ED Left-Without-Being-Seen Revenue Loss Calculator | Beam",
    description:
      "Free ED LWBS revenue loss calculator using the ACEP crowding position statement and Falvo/Handel opportunity-cost methodology.",
  },
  "/tools/urgent-care-break-even": {
    path: "/tools/urgent-care-break-even",
    title: "Urgent Care Break-Even Calculator (Visits/Day) | Beam",
    description:
      "Free urgent care break-even calculator using the Urgent Care Association contribution-margin method. Model visits per day required for profitability.",
  },
  "/tools/ccm-rpm-revenue": {
    path: "/tools/ccm-rpm-revenue",
    title: "CCM & RPM Revenue Calculator (99490, 99457, 99454) | Beam",
    description:
      "Free CCM and RPM revenue calculator using 2025 Medicare PFS rates for CPT 99490, 99439, 99457, 99458, 99453, and 99454.",
  },
  "/tools/cha2ds2-vasc": {
    path: "/tools/cha2ds2-vasc",
    title: "CHA₂DS₂-VASc Score Calculator (AFib Stroke Risk) | Beam",
    description:
      "Free CHA₂DS₂-VASc calculator with live scoring, annual stroke-risk %, and one-click clinical-note copy for the chart. ACC/AHA/HRS 2019 thresholds.",
  },
  "/tools/corrected-calcium": {
    path: "/tools/corrected-calcium",
    title: "Corrected Calcium Calculator (Albumin-Adjusted) | Beam",
    description:
      "Free albumin-corrected calcium calculator using the Payne formula, with a one-click copy of the result for the chart. Includes normal ranges and interpretation.",
  },
  "/tools/anion-gap": {
    path: "/tools/anion-gap",
    title: "Anion Gap Calculator (with Albumin Correction) | Beam",
    description:
      "Free anion gap calculator with optional potassium and albumin correction. Live results, plain-language interpretation, and one-click copy for the chart.",
  },
};


/**
 * Build SEO metadata for a dynamic RHTP state route, e.g. /rhtp/texas.
 */
export const rhtpStateSeo = (stateName: string, slug: string): RouteSeo => ({
  path: `/rhtp/${slug}`,
  title: `${stateName} Rural Health Transformation Program: Funding & Plan`,
  description: `How much ${stateName} gets from the Rural Health Transformation Program, what the state plan funds, and how rural hospitals can use it for specialist care.`,
});

/**
 * Build SEO metadata for a dynamic CAH directory state route,
 * e.g. /critical-access-hospitals/iowa.
 */
export const cahStateSeo = (
  stateName: string,
  slug: string,
  count: number
): RouteSeo => ({
  path: `/critical-access-hospitals/${slug}`,
  title: `Critical Access Hospitals in ${stateName}: ${count} Hospitals`.slice(0, 60),
  description: `Find ${count} critical access hospitals in ${stateName}. CMS list with cities, counties, ownership and ER services; retrieved August 18, 2026.`,
});


/** SEO metadata for /pace-organizations/:state. */
export const paceStateSeo = (stateName: string, slug: string, count: number): RouteSeo => ({
  path: `/pace-organizations/${slug}`,
  title: `PACE Programs in ${stateName}: ${count} CMS ${count === 1 ? "Contract" : "Contracts"}`.slice(0, 60),
  description: `Find ${count} PACE contracts in ${stateName}: locations, enrollment and profiles. CMS data: September 2026. Ask the program if it serves your ZIP code.`,
});

/** SEO metadata for /pace-organizations/:state/:org. */
export const paceOrgSeo = (
  o: { name: string; city: string; state: string; since: string; enrollment: number | null },
  path: string,
): RouteSeo => ({
  path,
  title: `${o.name} — ${o.city}, ${o.state} PACE Program`.slice(0, 60),
  description: `${o.name} in ${o.city}, ${o.state}: PACE program since ${o.since}${o.enrollment ? `, about ${o.enrollment.toLocaleString()} people enrolled` : ""}. Services, size, and how it compares, from CMS data.`.slice(0, 160),
});

/**
 * Resolve SEO config for an arbitrary path. Returns the homepage default
 * if no match. Handles dynamic /rhtp/:state via the optional resolver.
 */
export const getRouteSeo = (
  path: string,
  rhtpResolver?: (slug: string) => { stateName: string; slug: string } | null
): RouteSeo => {
  const exact = ROUTE_SEO[path];
  if (exact) return exact;

  const rhtpMatch = path.match(/^\/rhtp\/([^/]+)\/?$/);
  if (rhtpMatch && rhtpResolver) {
    const resolved = rhtpResolver(rhtpMatch[1]);
    if (resolved) return rhtpStateSeo(resolved.stateName, resolved.slug);
  }

  return { path, title: "Page not found | Beam Healthcare", description: "This page could not be found. Visit Beam Healthcare to find the care and resources you need.", noindex: true };
};

export const careerSeo = (spec: CareerSpec): RouteSeo => ({
  path: `/careers/${spec.slug}`,
  title: `${spec.seoName} Jobs | Beam Healthcare`,
  description: spec.category === "allied"
    ? `Join Beam's remote ${spec.seoName.toLowerCase()} panel. Active US license or credential and 3+ years of post-credential experience required.`
    : `Join Beam's remote ${spec.seoName.toLowerCase()} panel. Board-certified, NPI-verified physicians with 3+ years post-residency experience.`,
});

for (const spec of CAREER_SPECS) {
  const seo = careerSeo(spec);
  ROUTE_SEO[seo.path] = seo;
}
for (const entry of WHAT_IS_ENTRIES) {
  const path = `/what-is/${entry.slug}`;
  ROUTE_SEO[path] = { path, title: entry.title, description: entry.description };
}
ROUTE_SEO["/radar"] = {
  path: "/radar", title: "RHTP Opportunity Radar — Beam Healthcare",
  description: "Daily intel on rural health transformation funding. Federal opportunities tracked and scored for fit. Free, no signup required.",
};
ROUTE_SEO["/enterprise/occupational-health"] = {
  path: "/enterprise/occupational-health", title: "Specialist Access for Occupational Health | Beam",
  description: "Beam gives occupational health teams same-day specialist input: curbside answers, written eConsults, and virtual respiratory care behind your brand.",
};
for (const path of ["/groups/pace/tele-rt-monitoring", "/groups/pace/tele-rt-monitoring-v2"]) {
  ROUTE_SEO[path] = {
    path, title: "Tele-RT COPD Pilot for PACE | Beam Healthcare",
    description: "Apply for a six-month COPD pilot for your PACE program. Respiratory checks at your care center, with home support when needed.", noindex: true,
  };
}
